import { z } from 'zod'
import { EnvSchema, MiharuConfigSchema, MiharuConfigError, type MiharuConfig } from '@/types/config'

// Environment variable parsing and validation
export function parseEnvironment(): Partial<MiharuConfig> {
  const env = EnvSchema.parse(process.env)
  
  const config: Partial<MiharuConfig> = {}
  
  // Map environment variables to config
  if (env.MIHARU_ADAPTER) config.adapter = env.MIHARU_ADAPTER
  if (env.MIHARU_INSTANCE_ID) config.instanceId = env.MIHARU_INSTANCE_ID
  if (env.MIHARU_ENABLED_PROVIDERS) config.enabledProviders = env.MIHARU_ENABLED_PROVIDERS
  
  // Build options based on adapter
  if (env.MIHARU_ADAPTER === 'supabase' && env.MIHARU_SUPABASE_URL && env.MIHARU_SUPABASE_ANON_KEY) {
    config.options = {
      url: env.MIHARU_SUPABASE_URL,
      apiKey: env.MIHARU_SUPABASE_ANON_KEY,
      serviceRoleKey: env.MIHARU_SUPABASE_SERVICE_ROLE_KEY,
      connectionTimeout: 5000,
      maxRetries: 3,
      enableRLS: true,
    }
  } else if (env.MIHARU_ADAPTER === 'sqlite' && env.MIHARU_SQLITE_FILENAME) {
    config.options = {
      filename: env.MIHARU_SQLITE_FILENAME,
      readonly: env.MIHARU_SQLITE_READONLY ?? false,
      enableWAL: true,
      busyTimeout: 5000,
      maxRetries: 3,
    }
  }
  
  // Build batch config
  if (env.MIHARU_BATCH_SIZE || env.MIHARU_FLUSH_INTERVAL || env.MIHARU_MEMORY_THRESHOLD_MB) {
    config.batchConfig = {
      minSize: 10,
      maxSize: env.MIHARU_BATCH_SIZE ?? 100,
      flushInterval: env.MIHARU_FLUSH_INTERVAL ?? 5000,
      memoryThresholdMB: env.MIHARU_MEMORY_THRESHOLD_MB ?? 8,
      overflowStrategy: 'flush',
    }
  }
  
  // Build security config
  if (env.MIHARU_ENABLE_ENCRYPTION || env.MIHARU_ENCRYPTION_KEY || env.MIHARU_SANITIZE_PII !== undefined) {
    config.security = {
      enableEncryption: env.MIHARU_ENABLE_ENCRYPTION ?? false,
      encryptionKey: env.MIHARU_ENCRYPTION_KEY,
      sanitizePII: env.MIHARU_SANITIZE_PII,
      filePermissions: '600',
    }
  }
  
  // Build feature flags
  config.featureFlags = {
    enableStreaming: env.MIHARU_ENABLE_STREAMING,
    enableBatching: env.MIHARU_ENABLE_BATCHING,
    enableAutoRecovery: env.MIHARU_ENABLE_AUTO_RECOVERY,
    enablePerformanceMonitoring: env.MIHARU_ENABLE_PERFORMANCE_MONITORING,
    enableDistributedTracing: false,
    enableMetricsDashboard: false,
  }
  
  // Build logging config
  config.logging = {
    level: env.MIHARU_LOG_LEVEL,
    format: 'pretty',
    destination: 'console',
    maxFileSize: 10485760,
    maxFiles: 3,
  }
  
  return config
}

// Configuration validation and merging
export function validateConfig(userConfig: unknown = {}): MiharuConfig {
  try {
    // Parse environment variables
    const envConfig = parseEnvironment()
    
    // Merge user config with environment config (user config takes precedence)
    const userConfigTyped = userConfig as Partial<MiharuConfig>
    const mergedConfig = {
      ...envConfig,
      ...userConfigTyped,
      // Deep merge nested objects
      batchConfig: { ...envConfig.batchConfig, ...userConfigTyped?.batchConfig },
      sessionConfig: { ...envConfig.sessionConfig, ...userConfigTyped?.sessionConfig },
      retryConfig: { ...envConfig.retryConfig, ...userConfigTyped?.retryConfig },
      security: { ...envConfig.security, ...userConfigTyped?.security },
      featureFlags: { ...envConfig.featureFlags, ...userConfigTyped?.featureFlags },
      logging: { ...envConfig.logging, ...userConfigTyped?.logging },
    }
    
    return MiharuConfigSchema.parse(mergedConfig)
  } catch (error) {
    if (error instanceof z.ZodError) {
      const formattedErrors = error.errors.map(err => ({
        path: err.path.join('.'),
        message: err.message,
        received: 'received' in err ? err.received : undefined,
      }))
      throw new MiharuConfigError('Invalid configuration', formattedErrors)
    }
    throw error
  }
}

// Environment variable documentation generator
export function generateEnvDocs(): string {
  const docs = `
# MiharuAI Environment Variables

## Core Configuration
- MIHARU_ADAPTER: Storage adapter to use (supabase|sqlite|memory)
- MIHARU_INSTANCE_ID: Unique identifier for this instance
- MIHARU_LOG_LEVEL: Logging level (debug|info|warn|error)

## Supabase Configuration
- MIHARU_SUPABASE_URL: Supabase project URL
- MIHARU_SUPABASE_ANON_KEY: Supabase anonymous key
- MIHARU_SUPABASE_SERVICE_ROLE_KEY: Supabase service role key (optional)

## SQLite Configuration  
- MIHARU_SQLITE_FILENAME: SQLite database file path
- MIHARU_SQLITE_READONLY: Enable readonly mode (true|false)

## Performance Configuration
- MIHARU_BATCH_SIZE: Maximum batch size for data processing
- MIHARU_FLUSH_INTERVAL: Batch flush interval in milliseconds
- MIHARU_MEMORY_THRESHOLD_MB: Memory threshold for forced flush

## Security Configuration
- MIHARU_ENABLE_ENCRYPTION: Enable data encryption (true|false)
- MIHARU_ENCRYPTION_KEY: Encryption key (minimum 32 characters)
- MIHARU_SANITIZE_PII: Enable PII sanitization (true|false)

## Feature Flags
- MIHARU_ENABLE_STREAMING: Enable streaming support (true|false)
- MIHARU_ENABLE_BATCHING: Enable batch processing (true|false)
- MIHARU_ENABLE_AUTO_RECOVERY: Enable automatic error recovery (true|false)
- MIHARU_ENABLE_PERFORMANCE_MONITORING: Enable performance monitoring (true|false)

## Provider Configuration
- MIHARU_ENABLED_PROVIDERS: Comma-separated list of enabled providers (openai,anthropic,google)
`
  
  return docs.trim()
}